/**
 * LeaseForm Component
 * Form for creating and editing leases
 */

import { useState, useEffect, FormEvent } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePicker.css';
import type { LeaseDetail, CreateLeaseInput, UpdateLeaseInput, LeaseValidationWarning } from '../types/lease';
import type { TenantWithDetails } from '../lib/api';
import { createTenant } from '../lib/api';

interface LeaseFormProps {
  mode: 'create' | 'edit';
  lease?: LeaseDetail;
  unitId: string;
  availableTenants: TenantWithDetails[];
  // Create-mode seed values (dollar amounts, NOT cents). Used to pre-fill the Bring-Your-Own
  // form from move-in suggestions. Ignored in edit mode (the lease values win).
  initialValues?: {
    startDate?: string;
    rentAmount?: number;
    securityDeposit?: number;
    tenantIds?: string[];
  };
  onSubmit: (data: CreateLeaseInput | UpdateLeaseInput, acknowledgeWarnings: boolean) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  warnings?: LeaseValidationWarning[];
  submitError?: string | null;
  onClearError?: () => void;
}

interface FormData {
  startDate: string;
  endDate: string;
  rentAmount: string;
  securityDeposit: string;
  tenantIds: string[];
}

export function LeaseForm({
  mode,
  lease,
  unitId,
  availableTenants,
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  warnings = [],
  submitError = null,
  onClearError,
}: LeaseFormProps) {
  const [formData, setFormData] = useState<FormData>({
    startDate: lease?.startDate?.substring(0, 10) || initialValues?.startDate || '',
    endDate: lease?.endDate?.substring(0, 10) || '',
    rentAmount: lease?.rentAmount != null
      ? (lease.rentAmount / 100).toString()
      : initialValues?.rentAmount != null ? String(initialValues.rentAmount) : '',
    securityDeposit: lease?.securityDeposit != null
      ? (lease.securityDeposit / 100).toString()
      : initialValues?.securityDeposit != null ? String(initialValues.securityDeposit) : '',
    tenantIds: lease?.tenants?.map(t => t.id) || initialValues?.tenantIds || [],
  });

  const parseDateStr = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined;
    const parts = dateStr.split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showWarnings, setShowWarnings] = useState(false);

  // Inline tenant creation
  const [showInlineTenantForm, setShowInlineTenantForm] = useState(false);
  const [inlineTenantData, setInlineTenantData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [inlineTenantError, setInlineTenantError] = useState<string | null>(null);
  const [isCreatingTenant, setIsCreatingTenant] = useState(false);
  const [localAvailableTenants, setLocalAvailableTenants] = useState(availableTenants);

  useEffect(() => {
    if (warnings.length > 0) {
      setShowWarnings(true);
    }
  }, [warnings]);

  useEffect(() => {
    setLocalAvailableTenants(availableTenants);
  }, [availableTenants]);

  const handleCreateInlineTenant = async () => {
    setInlineTenantError(null);

    if (!inlineTenantData.firstName || !inlineTenantData.lastName || !inlineTenantData.email) {
      setInlineTenantError('First name, last name, and email are required');
      return;
    }

    try {
      setIsCreatingTenant(true);
      const newTenant = await createTenant(unitId, {
        firstName: inlineTenantData.firstName,
        lastName: inlineTenantData.lastName,
        email: inlineTenantData.email,
        phone: inlineTenantData.phone || undefined,
      });

      // Add new tenant to local list
      setLocalAvailableTenants([...localAvailableTenants, newTenant]);

      // Auto-select the new tenant
      setFormData(prev => ({
        ...prev,
        tenantIds: [...prev.tenantIds, newTenant.id],
      }));

      // Reset form
      setInlineTenantData({ firstName: '', lastName: '', email: '', phone: '' });
      setShowInlineTenantForm(false);
    } catch (err) {
      setInlineTenantError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setIsCreatingTenant(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.rentAmount || parseFloat(formData.rentAmount) <= 0) {
      newErrors.rentAmount = 'Rent amount must be greater than 0';
    }

    if (!formData.securityDeposit || parseFloat(formData.securityDeposit) < 0) {
      newErrors.securityDeposit = 'Security deposit cannot be negative';
    }

    if (formData.tenantIds.length === 0) {
      newErrors.tenantIds = 'At least one tenant is required';
    }

    // The end date may be on or after the start date (a one-day lease is valid), matching the
    // API's END_BEFORE_START rule.
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end < start) {
        newErrors.endDate = 'End date must be on or after the start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent, acknowledgeWarnings = false) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData = mode === 'create' ? ({
      startDate: formData.startDate,
      endDate: formData.endDate || undefined,
      rentAmount: Math.round(parseFloat(formData.rentAmount) * 100),
      securityDeposit: Math.round(parseFloat(formData.securityDeposit) * 100),
      tenantIds: formData.tenantIds,
      acknowledgeWarnings,
    } as CreateLeaseInput) : ({
      startDate: formData.startDate,
      endDate: formData.endDate || null,
      rentAmount: Math.round(parseFloat(formData.rentAmount) * 100),
      securityDeposit: Math.round(parseFloat(formData.securityDeposit) * 100),
      tenantIds: formData.tenantIds,
      acknowledgeWarnings,
    } as UpdateLeaseInput);

    await onSubmit(submitData, acknowledgeWarnings);
  };

  const handleTenantToggle = (tenantId: string) => {
    setFormData(prev => ({
      ...prev,
      tenantIds: prev.tenantIds.includes(tenantId)
        ? prev.tenantIds.filter(id => id !== tenantId)
        : [...prev.tenantIds, tenantId],
    }));
  };

  return (
    <form onSubmit={(e) => handleSubmit(e, false)} className="flex flex-col gap-6">
      <h3 className="text-xl font-semibold text-foreground m-0">
        {mode === 'create' ? 'Create New Lease' : 'Edit Lease'}
      </h3>

      {/* Error Display */}
      {submitError && (
        <div className="flex items-start justify-between gap-3 px-4 py-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md">
          <div className="flex items-start gap-3 flex-1">
            <span className="text-lg flex-shrink-0 mt-0.5">⚠️</span>
            <div className="text-sm text-destructive leading-[1.4]">{submitError}</div>
          </div>
          <button
            type="button"
            onClick={() => onClearError?.()}
            className="bg-transparent border-none text-lg text-muted-foreground cursor-pointer p-0 w-5 h-5 flex items-center justify-center flex-shrink-0 rounded hover:bg-accent"
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {/* Warnings Display */}
      {showWarnings && warnings.length > 0 && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="mb-3">
            <span className="text-sm font-semibold text-warning">⚠️ Warnings</span>
          </div>
          <ul className="m-0 mb-4 pl-5">
            {warnings.map((warning, idx) => (
              <li key={idx} className="text-sm text-warning mb-1">
                {warning.message}
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md cursor-pointer hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={(e) => handleSubmit(e as FormEvent<HTMLButtonElement>, true)}
              className="px-5 py-2.5 text-sm font-medium text-warning-foreground bg-warning border-none rounded-md cursor-pointer hover:bg-warning/90 disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
              disabled={isSubmitting}
            >
              Proceed Anyway
            </button>
          </div>
        </div>
      )}

      {/* Dates Section */}
      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-semibold text-foreground m-0">Lease Period</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="startDate" className="text-sm font-medium text-muted-foreground">
              Start Date *
            </label>
            <DatePicker
              id="startDate"
              selected={parseDateStr(formData.startDate) ?? null}
              onChange={(date: Date | null) => {
                const dateStr = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFormData({ ...formData, startDate: dateStr });
              }}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select start date"
              className={errors.startDate ? 'date-picker-error' : 'date-picker'}
              disabled={isSubmitting}
            />
            {errors.startDate && <span className="text-xs text-destructive">{errors.startDate}</span>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="endDate" className="text-sm font-medium text-muted-foreground">
              End Date (optional)
            </label>
            <DatePicker
              id="endDate"
              selected={parseDateStr(formData.endDate) ?? null}
              onChange={(date: Date | null) => {
                const dateStr = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFormData({ ...formData, endDate: dateStr });
              }}
              dateFormat="MM/dd/yyyy"
              placeholderText="Select end date"
              className={errors.endDate ? 'date-picker-error' : 'date-picker'}
              disabled={isSubmitting}
              minDate={parseDateStr(formData.startDate)}
            />
            {errors.endDate && <span className="text-xs text-destructive">{errors.endDate}</span>}
          </div>
        </div>
      </div>

      {/* Financial Section */}
      <div className="flex flex-col gap-3">
        <h4 className="text-sm font-semibold text-foreground m-0">Financial Terms</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="rentAmount" className="text-sm font-medium text-muted-foreground">
              Monthly Rent *
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-3 text-sm text-muted-foreground pointer-events-none">$</span>
              <input
                id="rentAmount"
                type="number"
                step="0.01"
                min="0"
                value={formData.rentAmount}
                onChange={(e) => setFormData({ ...formData, rentAmount: e.target.value })}
                className={`pl-7 pr-3 py-2 text-sm border rounded-md outline-none w-full disabled:bg-accent/50 disabled:cursor-not-allowed ${errors.rentAmount ? 'border-destructive' : 'border-input'}`}
                disabled={isSubmitting}
                data-testid="lease-rent-input"
              />
            </div>
            {errors.rentAmount && <span className="text-xs text-destructive">{errors.rentAmount}</span>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="securityDeposit" className="text-sm font-medium text-muted-foreground">
              Security Deposit *
            </label>
            <div className="relative flex items-center">
              <span className="absolute left-3 text-sm text-muted-foreground pointer-events-none">$</span>
              <input
                id="securityDeposit"
                type="number"
                step="0.01"
                min="0"
                value={formData.securityDeposit}
                onChange={(e) => setFormData({ ...formData, securityDeposit: e.target.value })}
                className={`pl-7 pr-3 py-2 text-sm border rounded-md outline-none w-full disabled:bg-accent/50 disabled:cursor-not-allowed ${errors.securityDeposit ? 'border-destructive' : 'border-input'}`}
                disabled={isSubmitting}
                data-testid="lease-deposit-input"
              />
            </div>
            {errors.securityDeposit && <span className="text-xs text-destructive">{errors.securityDeposit}</span>}
          </div>
        </div>
      </div>

      {/* Tenants Section */}
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-semibold text-foreground m-0">Tenants *</h4>
          {!showInlineTenantForm && (
            <button
              type="button"
              onClick={() => setShowInlineTenantForm(true)}
              className="px-3 py-1.5 text-[13px] font-medium text-primary bg-transparent border border-primary rounded-md cursor-pointer hover:bg-primary/5 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isSubmitting}
              data-testid="add-new-tenant-button"
            >
              + Add New Tenant
            </button>
          )}
        </div>

        {localAvailableTenants.length === 0 && !showInlineTenantForm ? (
          <p className="text-sm text-muted-foreground text-center p-4">No tenants available. Click"Add New Tenant" to create one.</p>
        ) : (
          <>
            {showInlineTenantForm && (
              <div className="p-4 bg-accent/50 rounded-md mb-4 flex flex-col gap-3">
                <h5 className="m-0 mb-2 text-sm font-semibold text-foreground">Create New Tenant</h5>
                {inlineTenantError && (
                  <div className="px-3 py-2 text-[13px] text-destructive bg-destructive/5 border border-destructive/20 rounded">
                    {inlineTenantError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-muted-foreground">First Name *</label>
                    <input
                      type="text"
                      value={inlineTenantData.firstName}
                      onChange={(e) => setInlineTenantData({ ...inlineTenantData, firstName: e.target.value })}
                      className="px-3 py-2 text-sm border border-input rounded-md outline-none disabled:bg-accent/50 disabled:cursor-not-allowed"
                      disabled={isCreatingTenant}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-muted-foreground">Last Name *</label>
                    <input
                      type="text"
                      value={inlineTenantData.lastName}
                      onChange={(e) => setInlineTenantData({ ...inlineTenantData, lastName: e.target.value })}
                      className="px-3 py-2 text-sm border border-input rounded-md outline-none disabled:bg-accent/50 disabled:cursor-not-allowed"
                      disabled={isCreatingTenant}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-muted-foreground">Email *</label>
                    <input
                      type="email"
                      value={inlineTenantData.email}
                      onChange={(e) => setInlineTenantData({ ...inlineTenantData, email: e.target.value })}
                      className="px-3 py-2 text-sm border border-input rounded-md outline-none disabled:bg-accent/50 disabled:cursor-not-allowed"
                      disabled={isCreatingTenant}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <input
                      type="tel"
                      value={inlineTenantData.phone}
                      onChange={(e) => setInlineTenantData({ ...inlineTenantData, phone: e.target.value })}
                      className="px-3 py-2 text-sm border border-input rounded-md outline-none disabled:bg-accent/50 disabled:cursor-not-allowed"
                      disabled={isCreatingTenant}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInlineTenantForm(false);
                      setInlineTenantData({ firstName: '', lastName: '', email: '', phone: '' });
                      setInlineTenantError(null);
                    }}
                    className="px-5 py-2.5 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md cursor-pointer hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
                    disabled={isCreatingTenant}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateInlineTenant}
                    className="px-4 py-2 text-[13px] font-medium text-success-foreground bg-success border-none rounded-md cursor-pointer hover:bg-success/90 disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
                    disabled={isCreatingTenant}
                  >
                    {isCreatingTenant ? 'Creating...' : 'Create Tenant'}
                  </button>
                </div>
              </div>
            )}

            {localAvailableTenants.length > 0 && (
              <div className="flex flex-col gap-2 max-h-48 overflow-y-auto p-2 border border-border rounded-md">
                {localAvailableTenants.map((tenant) => (
                  <label key={tenant.id} className="flex items-center gap-3 p-2 cursor-pointer rounded hover:bg-accent transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.tenantIds.includes(tenant.id)}
                      onChange={() => handleTenantToggle(tenant.id)}
                      className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isSubmitting}
                      data-testid={`lease-tenant-checkbox-${tenant.id}`}
                    />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-foreground">
                        {tenant.firstName} {tenant.lastName}
                      </span>
                      <span className="text-xs text-muted-foreground">{tenant.email}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </>
        )}
        {errors.tenantIds && <span className="text-xs text-destructive">{errors.tenantIds}</span>}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md cursor-pointer hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-5 py-2.5 text-sm font-medium text-primary-foreground bg-primary border-none rounded-md cursor-pointer hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
          disabled={isSubmitting}
          data-testid="lease-form-submit"
        >
          {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Lease' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
